import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Download, Activity, Users } from "lucide-react";

const DASHBOARD_IMG = "/screenshots/dashboard.png";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ downloads, visitors, onDownload, downloadAvailable }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const countLabel = downloads === null ? "— — —" : downloads.toLocaleString("en-US");
  const visitorLabel = visitors === null ? "— — —" : visitors.toLocaleString("en-US");

  return (
    <section ref={ref} id="top" className="relative pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="hero-section">
      <motion.div style={{ y: textY }} className="relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-8"
        >
          <span className="w-2 h-2 bg-accent" />
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground" data-testid="hero-overline">
            A YKD Enterprises Product
          </span>
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent" data-testid="hero-edition-badge">
            / IPO Intelligence Edition
          </span>
        </motion.div>

        <h1 className="font-display font-semibold tracking-tight [word-spacing:0.08em] leading-[0.95] text-[11vw] sm:text-6xl md:text-7xl lg:text-8xl" data-testid="hero-heading">
          <MaskedLine delay={0.25}>IPO</MaskedLine>
          <MaskedLine delay={0.38} className="text-accent">
            INTELLIGENCE
          </MaskedLine>
          <MaskedLine delay={0.51}>ENGINEERED.</MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end gap-8 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="max-w-md"
          >
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground" data-testid="hero-subtitle">
              YIIS-100 — the IPO Intelligence Edition of the YKD Investment
              Intelligence Suite — manages the complete IPO lifecycle: investment
              accounts, applications, allotment, listing, profit/loss and
              executive reporting.
            </p>
            <div className="mt-5 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground" data-testid="hero-release-line">
              v1.0 — Operation Blue / Windows Desktop
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="flex flex-col gap-4"
          >
            <button
              onClick={onDownload}
              disabled={downloadAvailable !== true}
              data-testid="hero-download-button"
              className="group flex items-center gap-3 px-8 py-4 bg-accent text-accent-foreground font-mono text-sm tracking-[0.15em] uppercase font-bold hover:bg-foreground hover:text-background transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-accent disabled:hover:text-accent-foreground"
            >
              <Download size={16} className="group-hover:translate-y-0.5 transition-transform duration-300" />
              {downloadAvailable === null ? "Connecting to download service..." : downloadAvailable ? "Download YIIS-100 .exe" : "Installer unavailable"}
            </button>

            <div
              className="flex flex-col gap-2 font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground"
              data-testid="download-counter-badge"
            >
              <span className="flex items-center gap-3">
                <Activity size={13} className="text-accent animate-pulse" />
                Total Downloads
                <span className="text-foreground font-bold tabular-nums" data-testid="download-count-value">
                  [ {countLabel} ]
                </span>
              </span>
              <span className="flex items-center gap-3">
                <Users size={13} className="text-accent" />
                Unique Visitors
                <span className="text-foreground font-bold tabular-nums" data-testid="visitor-count-value">
                  [ {visitorLabel} ]
                </span>
              </span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="mt-16 md:mt-24 relative"
      >
        <div className="border border-border overflow-hidden">
          <img
            src={DASHBOARD_IMG}
            alt="YIIS-100 IPO Intelligence Edition master dashboard"
            data-testid="hero-product-image"
            className="w-full h-auto block"
          />
        </div>
        <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-background/80 backdrop-blur px-3 py-1.5 border border-border">
          YIIS-100 / MASTER DASHBOARD — OPERATION BLUE
        </div>
      </motion.div>
    </section>
  );
};
